import React, { useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import { CloudUpload, Delete, InsertDriveFile } from '@mui/icons-material';
import { SxProps, Theme } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import { getFileType, getFilePreview, getFileSize, getFileName } from '@/utils/getFilePreview';
import { IFile } from '@/types';

export interface FileUploadProps {
  files: (File | IFile)[];
  onAdd: (newFiles: File[]) => void;
  onRemove: (index: number, file: File | IFile) => void;
  uploadUrl?: string;
  accept?: Record<string, string[]>;
  maxSize?: number;
  multiple?: boolean;
  maxFiles?: number;
  placeholder?: string;
  title?: React.ReactNode;
  dropzoneSx?: SxProps<Theme>;
  showPreview?: boolean;
  avatarSize?: number;
  fileNameLength?: number;
  onDropRejected?: (fileRejections: any[]) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  files,
  onAdd,
  onRemove,
  uploadUrl = '',
  accept = {
    'image/*': [],
    'application/pdf': [],
  },
  maxSize = 20 * 1024 * 1024,
  multiple = true,
  maxFiles,
  placeholder = 'Drag/Drop files here or click to select files',
  title,
  dropzoneSx,
  showPreview = true,
  avatarSize = 32,
  fileNameLength = 15,
  onDropRejected,
}) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    onAdd(acceptedFiles);
  }, [onAdd]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDropRejected: onDropRejected as any,
    accept,
    maxSize,
    multiple,
    maxFiles,
    noClick: false,
    noKeyboard: true,
  });

  const getAvatarSrc = (file: File | IFile) => {
    if (!showPreview) return undefined;
    if (!getFileType(file as any).startsWith('image')) return undefined;

    if (file instanceof File) {
      return URL.createObjectURL(file);
    }

    return getFilePreview(file as any, uploadUrl) || undefined;
  };

  return (
    <Box>
      {title}
      <Paper
        {...getRootProps()}
        sx={{
          p: 2,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: 'background.default',
          border: '1.5px dashed',
          borderColor: 'primary.main',
          '&:hover': { backgroundColor: 'action.hover' },
          ...dropzoneSx,
        }}
      >
        <input {...getInputProps()} />
        <CloudUpload sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="body2">{placeholder}</Typography>
      </Paper>

      {files.length > 0 && (
        <List sx={{ mt: 1 }}>
          {files.map((file, index) => (
            <ListItem
              key={`${getFileName(file as any)}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 0,
              }}
            >
              <ListItemAvatar sx={{ minWidth: 45 }}>
                {getFileType(file as any).startsWith('image') ? (
                  <Avatar
                    variant="square"
                    src={getAvatarSrc(file)}
                    sx={{ width: avatarSize, height: avatarSize }}
                  />
                ) : (
                  <Avatar sx={{ width: avatarSize, height: avatarSize }}>
                    <InsertDriveFile fontSize="small" />
                  </Avatar>
                )}
              </ListItemAvatar>

              <ListItemText
                primary={getFileName(file as any).slice(0, fileNameLength)}
                secondary={`${getFileSize(file as any)} MB`}
                primaryTypographyProps={{ fontSize: '0.875rem' }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />

              <IconButton
                color="error"
                onClick={() => onRemove(index, file)}
                size="small"
                sx={{
                  bgcolor: 'error.main',
                  padding: '4px',
                  color: 'error.contrastText',
                  '&:hover': {
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                  },
                }}
              >
                <Delete sx={{ fontSize: '14px' }} />
              </IconButton>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default FileUpload;
